import { Injectable, inject, signal } from '@angular/core';
import { LoggerService } from './logger.service';

export interface CachedResponse<T = any> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
  key: string;
}

export type CacheStrategy = 'cache-first' | 'network-first' | 'cache-only' | 'network-only';

const STORAGE_KEY_PREFIX = 'cartco.cache.';
const MAX_CACHE_SIZE = 50; // Maximum number of cached items
const MAX_ITEM_SIZE_BYTES = 500 * 1024; // 500 KB - skip caching larger payloads to avoid QuotaExceededError
const DEFAULT_TTL = 60 * 60 * 1000; // 1 hour default TTL

@Injectable({ providedIn: 'root' })
export class OfflineCacheService {
  private readonly logger = inject(LoggerService);
  private readonly cacheSize = signal<number>(0);

  readonly cacheCount = this.cacheSize.asReadonly();

  /**
   * Generate cache key from URL and params
   */
  private generateCacheKey(url: string, params?: Record<string, any>): string {
    const paramsStr = params
      ? Object.keys(params)
          .sort()
          .map((key) => `${key}=${params[key]}`)
          .join('&')
      : '';
    return `${url}${paramsStr ? `?${paramsStr}` : ''}`;
  }

  /**
   * Get storage key for cache entry
   */
  private getStorageKey(cacheKey: string): string {
    // Create a safe key by encoding it
    const encoded = btoa(cacheKey).replace(/[+/=]/g, (m) => {
      return { '+': '-', '/': '_', '=': '' }[m] || '';
    });
    return `${STORAGE_KEY_PREFIX}${encoded}`;
  }

  /**
   * Get cached response if available and not expired
   */
  get<T>(url: string, params?: Record<string, any>): T | null {
    try {
      const cacheKey = this.generateCacheKey(url, params);
      const storageKey = this.getStorageKey(cacheKey);

      if (typeof window === 'undefined' || !window.localStorage) {
        return null;
      }

      const stored = localStorage.getItem(storageKey);
      if (!stored) {
        return null;
      }

      const cached: CachedResponse<T> = JSON.parse(stored);
      const now = Date.now();

      // Check if expired
      if (now - cached.timestamp > cached.ttl) {
        this.logger.debug(`[OfflineCache] Cache expired for ${url}`);
        this.remove(url, params);
        return null;
      }

      this.logger.debug(`[OfflineCache] Cache hit for ${url}`);
      return cached.data;
    } catch (error) {
      this.logger.error(`[OfflineCache] Error reading cache for ${url}`, error);
      return null;
    }
  }

  /**
   * Set cached response.
   * Skips caching if payload exceeds MAX_ITEM_SIZE_BYTES to avoid QuotaExceededError.
   */
  set<T>(url: string, data: T, ttl: number = DEFAULT_TTL, params?: Record<string, any>): void {
    const cacheKey = this.generateCacheKey(url, params);
    const cached: CachedResponse<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      key: cacheKey,
    };
    const serialized = JSON.stringify(cached);

    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    // Skip caching if single item would exceed size limit (prevents QuotaExceededError)
    if (serialized.length > MAX_ITEM_SIZE_BYTES) {
      this.logger.debug(`[OfflineCache] Skipping cache for ${url} (payload ${(serialized.length / 1024).toFixed(1)} KB > ${MAX_ITEM_SIZE_BYTES / 1024} KB limit)`);
      return;
    }

    try {
      const storageKey = this.getStorageKey(cacheKey);

      // Check cache size limit
      this.enforceCacheSizeLimit();

      localStorage.setItem(storageKey, serialized);
      this.updateCacheSize();

      this.logger.debug(`[OfflineCache] Cached response for ${url}`, { ttl: ttl / 1000 / 60 });
    } catch (error) {
      this.logger.error(`[OfflineCache] Error caching response for ${url}`, error);

      const isQuotaExceeded =
        error instanceof DOMException &&
        (error.code === 22 || (error as DOMException).name === 'QuotaExceededError');

      if (isQuotaExceeded) {
        this.logger.warn('[OfflineCache] Storage full, clearing old entries');
        this.clearExpired();
        // Do not retry if this single item is too large
        if (serialized.length > MAX_ITEM_SIZE_BYTES) {
          this.logger.debug(`[OfflineCache] Not retrying: item size exceeds limit`);
          return;
        }
        this.clearOldestEntries(5);
        try {
          localStorage.setItem(this.getStorageKey(cacheKey), serialized);
          this.updateCacheSize();
          this.logger.debug(`[OfflineCache] Cached response for ${url} after cleanup`);
        } catch (retryError) {
          this.logger.error('[OfflineCache] Still unable to cache after cleanup', retryError);
        }
      }
    }
  }

  /**
   * Remove cached response
   */
  remove(url: string, params?: Record<string, any>): void {
    try {
      const cacheKey = this.generateCacheKey(url, params);
      const storageKey = this.getStorageKey(cacheKey);

      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(storageKey);
        this.updateCacheSize();
      }
    } catch (error) {
      this.logger.error(`[OfflineCache] Error removing cache for ${url}`, error);
    }
  }

  /**
   * Invalidate cache by URL pattern
   */
  invalidate(pattern: string | RegExp): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }

      const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
      const keysToRemove: string[] = [];

      // Iterate through all localStorage keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
          try {
            const cached: CachedResponse = JSON.parse(localStorage.getItem(key) || '{}');
            if (regex.test(cached.key)) {
              keysToRemove.push(key);
            }
          } catch {
            // Invalid cache entry, remove it
            keysToRemove.push(key);
          }
        }
      }

      keysToRemove.forEach((key) => localStorage.removeItem(key));
      this.updateCacheSize();

      this.logger.debug(`[OfflineCache] Invalidated ${keysToRemove.length} cache entries matching ${pattern}`);
    } catch (error) {
      this.logger.error(`[OfflineCache] Error invalidating cache for pattern ${pattern}`, error);
    }
  }

  /**
   * Clear all expired cache entries
   */
  clearExpired(): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }

      const now = Date.now();
      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
          try {
            const cached: CachedResponse = JSON.parse(localStorage.getItem(key) || '{}');
            if (now - cached.timestamp > cached.ttl) {
              keysToRemove.push(key);
            }
          } catch {
            // Invalid cache entry, remove it
            keysToRemove.push(key);
          }
        }
      }

      keysToRemove.forEach((key) => localStorage.removeItem(key));
      this.updateCacheSize();

      if (keysToRemove.length > 0) {
        this.logger.debug(`[OfflineCache] Cleared ${keysToRemove.length} expired cache entries`);
      }
    } catch (error) {
      this.logger.error('[OfflineCache] Error clearing expired cache', error);
    }
  }

  /**
   * Remove the oldest N cache entries to free storage (e.g. after QuotaExceededError).
   */
  private clearOldestEntries(count: number): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage || count <= 0) {
        return;
      }

      const entries: Array<{ key: string; timestamp: number }> = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
          try {
            const cached: CachedResponse = JSON.parse(localStorage.getItem(key) || '{}');
            entries.push({ key, timestamp: cached.timestamp });
          } catch {
            localStorage.removeItem(key);
          }
        }
      }

      entries.sort((a, b) => a.timestamp - b.timestamp);
      const toRemove = entries.slice(0, Math.min(count, entries.length));
      toRemove.forEach((entry) => localStorage.removeItem(entry.key));
      this.updateCacheSize();

      if (toRemove.length > 0) {
        this.logger.debug(`[OfflineCache] Cleared ${toRemove.length} oldest cache entries`);
      }
    } catch (error) {
      this.logger.error('[OfflineCache] Error clearing oldest entries', error);
    }
  }

  /**
   * Clear all cache entries
   */
  clearAll(): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }

      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => localStorage.removeItem(key));
      this.updateCacheSize();

      this.logger.debug(`[OfflineCache] Cleared all ${keysToRemove.length} cache entries`);
    } catch (error) {
      this.logger.error('[OfflineCache] Error clearing all cache', error);
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; entries: Array<{ key: string; age: number; ttl: number }> } {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return { size: 0, entries: [] };
      }

      const now = Date.now();
      const entries: Array<{ key: string; age: number; ttl: number }> = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
          try {
            const cached: CachedResponse = JSON.parse(localStorage.getItem(key) || '{}');
            entries.push({
              key: cached.key,
              age: now - cached.timestamp,
              ttl: cached.ttl,
            });
          } catch {
            // Skip invalid entries
          }
        }
      }

      return { size: entries.length, entries };
    } catch (error) {
      this.logger.error('[OfflineCache] Error getting cache stats', error);
      return { size: 0, entries: [] };
    }
  }

  /**
   * Enforce cache size limit by removing oldest entries
   */
  private enforceCacheSizeLimit(): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }

      const entries: Array<{ key: string; timestamp: number }> = [];

      for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i);
        if (storageKey && storageKey.startsWith(STORAGE_KEY_PREFIX)) {
          try {
            const cached: CachedResponse = JSON.parse(localStorage.getItem(storageKey) || '{}');
            entries.push({ key: storageKey, timestamp: cached.timestamp });
          } catch {
            // Invalid entry, remove it
            localStorage.removeItem(storageKey);
          }
        }
      }

      // If over limit, remove oldest entries
      if (entries.length >= MAX_CACHE_SIZE) {
        entries.sort((a, b) => a.timestamp - b.timestamp);
        const toRemove = entries.slice(0, entries.length - MAX_CACHE_SIZE + 1);
        toRemove.forEach((entry) => localStorage.removeItem(entry.key));
        this.logger.debug(`[OfflineCache] Removed ${toRemove.length} oldest cache entries to enforce size limit`);
      }
    } catch (error) {
      this.logger.error('[OfflineCache] Error enforcing cache size limit', error);
    }
  }

  /**
   * Update cache size signal
   */
  private updateCacheSize(): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        this.cacheSize.set(0);
        return;
      }

      let count = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
          count++;
        }
      }
      this.cacheSize.set(count);
    } catch (error) {
      this.cacheSize.set(0);
    }
  }

  /**
   * Initialize cache service (clear expired on startup)
   */
  initialize(): void {
    this.clearExpired();
    this.updateCacheSize();
  }
}

